import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { BANK_SCRAPING_IFRAME_STATUS } from 'consts/loan/AU';
import { trackProvisoIframeNavigation, trackProvisoStatus } from './trackProvisoStatus';

const IFRAME_RESPONSE_TIMEOUT = 20000;
const BARE_STATUS_REGEX = /^[A-Z_]+$/;

export const useProvisoIframeTracking = ({
  dispatch, hasIframeUrl, provisoUrl, retryKey,
}) => {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const iframeMountTimeRef = useRef(null);
  const hasIframeRespondedRef = useRef(false);
  const iframeLoadCountRef = useRef(0);
  const silenceWatchdogIdRef = useRef(null);
  const trackingSessionRef = useRef({ isCancelled: false });

  const provisoOrigin = useMemo(() => {
    try {
      return provisoUrl ? new URL(provisoUrl).origin : null;
    } catch (urlError) {
      return null;
    }
  }, [provisoUrl]);

  useEffect(() => {
    if (!hasIframeUrl) {
      return undefined;
    }

    iframeMountTimeRef.current = Date.now();
    hasIframeRespondedRef.current = false;
    iframeLoadCountRef.current = 0;

    const trackingSession = { isCancelled: false };
    trackingSessionRef.current = trackingSession;

    const noResponseWatchdogId = setTimeout(() => {
      const hasIframeLoaded = Boolean(iframeLoadCountRef.current);
      const isNoResponse = !hasIframeRespondedRef.current && !hasIframeLoaded;

      if (isNoResponse) {
        dispatch(trackProvisoStatus(BANK_SCRAPING_IFRAME_STATUS.IFRAME_NO_RESPONSE));
      }
    }, IFRAME_RESPONSE_TIMEOUT);

    return () => {
      trackingSession.isCancelled = true;
      clearTimeout(noResponseWatchdogId);
      clearTimeout(silenceWatchdogIdRef.current);
    };
  }, [dispatch, hasIframeUrl, retryKey]);

  const handleIframeLoad = useCallback(() => {
    setIsIframeLoaded(true);

    iframeLoadCountRef.current += 1;
    const isFirstLoad = iframeLoadCountRef.current === 1;
    const elapsedTime = iframeMountTimeRef.current && Date.now() - iframeMountTimeRef.current;

    if (isFirstLoad) {
      dispatch(trackProvisoStatus({
        status: BANK_SCRAPING_IFRAME_STATUS.IFRAME_RENDERED,
        status_message: elapsedTime,
      }));

      const trackingSession = trackingSessionRef.current;

      clearTimeout(silenceWatchdogIdRef.current);
      silenceWatchdogIdRef.current = setTimeout(() => {
        const reportSilenceStatus = (status) => {
          if (!trackingSession.isCancelled && !hasIframeRespondedRef.current) {
            dispatch(trackProvisoStatus(status));
          }
        };

        if (hasIframeRespondedRef.current) {
          return;
        }

        if (!provisoOrigin) {
          reportSilenceStatus(BANK_SCRAPING_IFRAME_STATUS.IFRAME_NO_RESPONSE);
          return;
        }

        fetch(provisoOrigin, { mode: 'no-cors', cache: 'no-store' })
          .then(() => reportSilenceStatus(BANK_SCRAPING_IFRAME_STATUS.IFRAME_SILENT))
          .catch(() => reportSilenceStatus(BANK_SCRAPING_IFRAME_STATUS.IFRAME_NO_RESPONSE));
      }, IFRAME_RESPONSE_TIMEOUT);

      return;
    }

    hasIframeRespondedRef.current = true;

    dispatch(trackProvisoIframeNavigation({
      navigationCount: iframeLoadCountRef.current - 1,
      elapsedTime,
    }));
  }, [dispatch, provisoOrigin]);

  const handleIframeError = useCallback(() => {
    hasIframeRespondedRef.current = true;

    dispatch(trackProvisoStatus(BANK_SCRAPING_IFRAME_STATUS.IFRAME_RENDER_FAIL));
  }, [dispatch]);

  const handleIframeMessage = useCallback((event) => {
    const shouldCancelHandler = !provisoOrigin || event.origin !== provisoOrigin;

    if (shouldCancelHandler) {
      return;
    }

    hasIframeRespondedRef.current = true;

    const messageData = (() => {
      const isStringType = typeof event.data === 'string';

      if (!isStringType) {
        return event.data;
      }

      const trimmedData = event.data.trim();
      const isBareStatus = BARE_STATUS_REGEX.test(trimmedData);

      if (isBareStatus) {
        return { status: trimmedData };
      }

      try {
        return JSON.parse(trimmedData);
      } catch (parseError) {
        dispatch(trackProvisoStatus(`Invalid message format: ${parseError?.message}`));
        return null;
      }
    })();

    const isMessageDataInvalid = !messageData || !messageData.status;

    if (isMessageDataInvalid) {
      return;
    }

    dispatch(trackProvisoStatus({ ...messageData }));
  }, [dispatch, provisoOrigin]);

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);

    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [handleIframeMessage]);

  const resetIframeLoaded = useCallback(() => {
    setIsIframeLoaded(false);
  }, []);

  return {
    isIframeLoaded,
    resetIframeLoaded,
    handleIframeLoad,
    handleIframeError,
  };
};
